import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  PortalDsAlert,
  portalDs,
} from 'src/components/portal-design-system';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from 'src/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'src/components/ui/tabs';
import { cn } from 'src/lib/utils';
import {
  isPortalSyncNotConfiguredError,
  useGetPortalSubscriptionQuery,
  useGetPortalUserQuery,
} from '../endpoints/portal-users.endpoints';
import { resolvePortalUserStatus } from '../utils/portal-users.mappers';
import type {
  AdminSettableSubscriptionTier,
  PortalUser,
} from '../types/users.types';
import {
  ACTIVITY_LOG_PAGE_SIZE,
  UserDetailUsageActivityTab,
} from './user-detail-usage-activity-tab';
import { UserDetailOverviewTab } from './user-detail-overview-tab';
import { UserManagementPanel } from './user-management-panel';

const DRAWER_TAB_TRIGGER_CLASS = cn(
  'rounded-none border-b-2 border-transparent bg-transparent px-0 pb-3 text-[14px] font-semibold text-[#5E6278] shadow-none',
  'hover:bg-transparent hover:text-[#0D0F1A]',
  '[&[aria-selected=true]]:border-[#0D0F1A] [&[aria-selected=true]]:bg-transparent [&[aria-selected=true]]:text-[#0D0F1A] [&[aria-selected=true]]:shadow-none',
  'mr-6 last:mr-0'
);

type DrawerTab = 'overview' | 'usage' | 'subscription';

interface UserDetailDrawerProps {
  userId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listUser?: PortalUser | null;
  actionsDisabled?: boolean;
  onSuspend: (userId: string) => Promise<void>;
  onReactivate: (userId: string) => Promise<void>;
  onBypassEmailVerification: (userId: string) => void;
  onSetBillingConversionDisabled: (userId: string, value: boolean) => void;
  onSetConversionModalHidden: (userId: string, value: boolean) => void;
  onAddTokens: (userId: string, amount: number) => Promise<{ ok: boolean; message?: string }>;
  onSaveSubscriptionTier: (
    workspaceId: string,
    tier: AdminSettableSubscriptionTier
  ) => Promise<{ ok: boolean; message?: string }>;
  onOpenTeam?: (workspaceId: string) => void;
}

export function UserDetailDrawer({
  userId,
  open,
  onOpenChange,
  listUser,
  actionsDisabled = false,
  onSuspend,
  onReactivate,
  onBypassEmailVerification,
  onSetBillingConversionDisabled,
  onSetConversionModalHidden,
  onAddTokens,
  onSaveSubscriptionTier,
  onOpenTeam,
}: UserDetailDrawerProps) {
  const [activeTab, setActiveTab] = useState<DrawerTab>('overview');
  const [activityPage, setActivityPage] = useState(1);

  useEffect(() => {
    if (open) {
      setActiveTab('overview');
      setActivityPage(1);
    }
  }, [open, userId]);

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetPortalUserQuery(
    {
      userId: userId ?? '',
      page: activityPage,
      page_size: ACTIVITY_LOG_PAGE_SIZE,
    },
    { skip: !open || !userId }
  );

  const listFallback = listUser && userId === listUser.id ? listUser : null;
  // Detail payloads can omit fields the list already has (e.g. subscription).
  // Flow may also return a nested { profile, workspace, activity_log } shape.
  // Flatten + merge list → detail so missing upstream fields do not crash the drawer.
  const flattenedDetail = data
    ? (() => {
      const nestedProfile = (data as { profile?: PortalUser }).profile;
      const nestedWorkspace = (data as {
        workspace?: { id?: string; name?: string } | null;
      }).workspace;
      const base = nestedProfile
        ? {
          ...nestedProfile,
          activity_log: data.activity_log,
        }
        : data;

      return {
        ...base,
        workspace_id: base.workspace_id
          ?? nestedWorkspace?.id
          ?? (nestedWorkspace as { workspace_id?: string } | null | undefined)?.workspace_id
          ?? null,
        workspace_name: base.workspace_name
          ?? nestedWorkspace?.name
          ?? (nestedWorkspace as { workspace_name?: string } | null | undefined)?.workspace_name
          ?? null,
      };
    })()
    : null;

  const rawUser = flattenedDetail
    ? {
      ...listFallback,
      ...flattenedDetail,
      subscription: flattenedDetail.subscription ?? listFallback?.subscription,
      workspace_id: flattenedDetail.workspace_id ?? listFallback?.workspace_id ?? null,
      workspace_name: flattenedDetail.workspace_name ?? listFallback?.workspace_name ?? null,
    }
    : listFallback
      ? {
        ...listFallback,
        activity_log: {
          count: 0,
          next: null,
          previous: null,
          results: [],
        },
      }
      : null;

  const user = rawUser ? {
    ...rawUser,
    status: resolvePortalUserStatus(rawUser),
  } : null;

  const isSyncNotConfigured = isError && isPortalSyncNotConfiguredError(error);
  const isActivityLoading = isFetching && !isLoading;

  const workspaceId = user?.workspace_id ?? null;
  const { data: subscriptionData } = useGetPortalSubscriptionQuery(
    workspaceId ?? '',
    { skip: !open || !workspaceId }
  );

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
  };

  return (
    <Sheet
      open={open}
      onOpenChange={handleOpenChange}
    >
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 overflow-y-auto bg-white p-0 sm:max-w-2xl"
      >
        <div className={portalDs.sheetHeader}>
          <SheetHeader className="space-y-1 text-left">
            <SheetTitle className={portalDs.sheetTitle}>
              {user?.name ?? 'User detail'}
            </SheetTitle>
            <SheetDescription className={portalDs.sheetDescription}>
              {user?.email ?? 'Review profile, usage, activity, and subscription settings.'}
            </SheetDescription>
          </SheetHeader>
        </div>

        <div className="flex-1 px-6 py-5">
          {isLoading ? (
            <div
              className={cn('flex items-center gap-2 py-8', portalDs.bodySm)}
              role="status"
            >
              <Loader2 className="size-4 animate-spin text-[#9CA3AF]" />
              Loading user detail…
            </div>
          ) : isSyncNotConfigured ? (
            <PortalDsAlert
              variant="warning"
              title="User management is unavailable"
            >
              Portal sync is not configured. User detail cannot be loaded.
            </PortalDsAlert>
          ) : isError ? (
            <PortalDsAlert
              variant="danger"
              title="Could not load user detail"
            >
              User data is temporarily unavailable. Try again shortly.
            </PortalDsAlert>
          ) : user ? (
            <Tabs
              value={activeTab}
              onValueChange={(value) => setActiveTab(value as DrawerTab)}
            >
              <TabsList className="mb-6 h-auto w-full justify-start rounded-none border-b border-[#EDEEF2] bg-transparent p-0">
                <TabsTrigger
                  value="overview"
                  className={DRAWER_TAB_TRIGGER_CLASS}
                >
                  Overview
                </TabsTrigger>
                <TabsTrigger
                  value="usage"
                  className={DRAWER_TAB_TRIGGER_CLASS}
                >
                  Usage &amp; Activity
                </TabsTrigger>
                <TabsTrigger
                  value="subscription"
                  className={DRAWER_TAB_TRIGGER_CLASS}
                >
                  Subscription &amp; Limits
                </TabsTrigger>
              </TabsList>

              <TabsContent
                value="overview"
                className="mt-0"
              >
                <UserDetailOverviewTab
                  user={user}
                  onOpenTeam={onOpenTeam}
                />
              </TabsContent>

              <TabsContent
                value="usage"
                className="mt-0"
              >
                <UserDetailUsageActivityTab
                  user={user}
                  activityLog={data?.activity_log ?? user.activity_log}
                  activityPage={activityPage}
                  onActivityPageChange={setActivityPage}
                  isActivityLoading={isActivityLoading}
                />
              </TabsContent>

              <TabsContent
                value="subscription"
                className="mt-0"
              >
                <UserManagementPanel
                  user={user}
                  disabled={actionsDisabled}
                  tierOptions={subscriptionData?.tier_options}
                  onSuspend={() => {
                    void onSuspend(user.id).then(() => refetch());
                  }}
                  onReactivate={() => {
                    void onReactivate(user.id).then(() => refetch());
                  }}
                  onBypassEmailVerification={() => {
                    onBypassEmailVerification(user.id);
                    void refetch();
                  }}
                  onSetBillingConversionDisabled={(value) => {
                    void onSetBillingConversionDisabled(user.id, value);
                    void refetch();
                  }}
                  onSetConversionModalHidden={(value) => {
                    void onSetConversionModalHidden(user.id, value);
                    void refetch();
                  }}
                  onAddTokens={async(amount) => {
                    const result = await onAddTokens(user.id, amount);
                    if (result.ok) {
                      void refetch();
                    }
                    return result;
                  }}
                  onSaveSubscriptionTier={async(workspaceId, tier) => {
                    const result = await onSaveSubscriptionTier(workspaceId, tier);
                    if (result.ok) {
                      void refetch();
                    }
                    return result;
                  }}
                />
              </TabsContent>
            </Tabs>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
